/**
 * 플랜 목록의 목업 사례.
 * 고른 동안만 이 화면에 겹쳐 보이고, 플랜에는 저장하지 않는다.
 */
import { useSyncExternalStore } from "react";
import { addDays } from "@/engine/date";
import type { PlanSnapshot, PlanTone } from "./plan-gallery";

export const PLAN_MOCK_CASES = [
  { id: "empty", label: "없음" },
  { id: "long", label: "많이" },
  { id: "current", label: "현재 데이터" },
] as const;

export type PlanMockCase = (typeof PLAN_MOCK_CASES)[number]["id"];

const LONG_NAME = "2026년 4분기 그로스 캠페인과 파트너 공동 프로모션 키비주얼 최종 확정과 모바일 홈 상단 교체";
const SHORT_NAMES = ["고객 마케팅 시스템", "제조사 ERP 구축", "콘텐츠 운영", "데이터 전환"];
const PEOPLE = ["김PM", "이디자인", "박엔진", "정프론트", "송DB", "한기획"];
const TONES: PlanTone[] = ["active", "active", "active", "late", "done", "empty", "active", "active"];

const MOCK_CASE_KEY = "plan-mock-case";

function readStoredCase(): PlanMockCase {
  try {
    const raw = sessionStorage.getItem(MOCK_CASE_KEY);
    if (raw === "empty" || raw === "long" || raw === "current") return raw;
  } catch {
    // 이 환경에는 sessionStorage가 없다.
  }
  return "current";
}

let current: PlanMockCase = readStoredCase();
const listeners = new Set<() => void>();

export function getPlanMockCase(): PlanMockCase {
  return current;
}

export function setPlanMockCase(next: PlanMockCase) {
  if (current === next) return;
  current = next;
  try {
    sessionStorage.setItem(MOCK_CASE_KEY, next);
  } catch {
    // 저장하지 못해도 이번 화면에는 적용한다.
  }
  for (const listener of listeners) listener();
}

export function usePlanMockCase(): PlanMockCase {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getPlanMockCase,
    getPlanMockCase,
  );
}

export function isPlanMockId(id: string): boolean {
  return id.startsWith("mock:");
}

export type PlanMockCard = {
  id: string;
  name: string;
  updatedAt: string;
  finish: string;
  tone: PlanTone;
  locked: boolean;
  snap: PlanSnapshot;
};

/** 많이는 제목이 긴 플랜이 스크롤될 만큼 있다. 없음은 호출하지 않는다. */
export function planMockCards(today: string): PlanMockCard[] {
  const out: PlanMockCard[] = [];
  for (let n = 0; n < 48; n += 1) {
    const archived = n % 8 === 7;
    const tone = archived ? "active" : TONES[n % TONES.length]!;
    const finish =
      tone === "empty" ? "" : tone === "late" ? addDays(today, -(3 + (n % 20))) : tone === "done" ? addDays(today, -(n % 15)) : addDays(today, 7 + (n % 40));
    const peopleCount = n % 5 === 0 ? 0 : n % 5 === 4 ? PEOPLE.length : n % 5;
    const day = addDays(today, -n);
    const hour = String(n % 9).padStart(2, "0");
    out.push({
      id: `mock:plan-${n}`,
      name: n % 8 === 1 ? SHORT_NAMES[n % SHORT_NAMES.length]! : n % 5 === 0 ? `${LONG_NAME} ${n + 1}` : LONG_NAME,
      updatedAt: `${day}T${hour}:00:00.000Z`,
      finish,
      tone,
      locked: n % 7 === 0,
      snap: {
        finish,
        pct: tone === "done" ? 100 : tone === "empty" ? 0 : tone === "late" ? 25 + (n % 40) : 10 + (n % 70),
        tone,
        people: PEOPLE.slice(0, peopleCount),
        taskCount: tone === "empty" ? 0 : 2 + (n % 18),
        lifecycle: archived ? "archived" : "active",
      },
    });
  }
  return out;
}
